import { getAccessToken, getUser, isSupabaseAuthConfigured } from './supabaseAuth';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

function withDefaults(planCode) {
  const code = String(planCode || 'explorer').toLowerCase();
  if (code === 'starter' || code === 'premium') return code;
  return 'explorer';
}

async function apiGetAuth(path) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${SUPABASE_URL}${path}`, {
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`
    }
  });

  const text = await response.text();
  if (!response.ok) {
    const err = new Error(text || 'Request failed');
    err.status = response.status;
    throw err;
  }
  return text ? JSON.parse(text) : null;
}

async function apiPostAuth(path, payload) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${SUPABASE_URL}${path}`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload || {})
  });

  const text = await response.text();
  if (!response.ok) {
    const err = new Error(text || 'Request failed');
    err.status = response.status;
    throw err;
  }
  return text ? JSON.parse(text) : null;
}

export async function fetchMyEffectivePlan() {
  if (!isSupabaseAuthConfigured()) return { planCode: 'explorer', status: 'active' };
  const user = getUser();
  if (!user?.id) return { planCode: 'explorer', status: 'active' };

  try {
    const rows = await apiGetAuth(
      '/rest/v1/rpc/get_my_effective_plan'
    );
    const row = Array.isArray(rows) ? rows[0] : rows;
    return {
      planCode: withDefaults(row?.plan_code),
      status: String(row?.subscription_status || 'active'),
      provider: '',
      externalSubscriptionId: '',
      externalCustomerId: '',
      cancelAtPeriodEnd: false
    };
  } catch {
    return { planCode: 'explorer', status: 'active', provider: '', externalSubscriptionId: '', externalCustomerId: '', cancelAtPeriodEnd: false };
  }
}

export async function fetchMySubscriptionDetails() {
  if (!isSupabaseAuthConfigured()) {
    return { planCode: 'explorer', status: 'active', provider: '', externalSubscriptionId: '', externalCustomerId: '', cancelAtPeriodEnd: false };
  }
  const user = getUser();
  if (!user?.id) {
    return { planCode: 'explorer', status: 'active', provider: '', externalSubscriptionId: '', externalCustomerId: '', cancelAtPeriodEnd: false };
  }

  try {
    let effectiveRows = null;
    try {
      effectiveRows = await apiGetAuth(
        '/rest/v1/user_subscriptions?select=plan_code,status,provider,external_subscription_id,external_customer_id,period_end,cancel_at_period_end,updated_at,created_at&status=in.(active,trialing)&order=updated_at.desc,created_at.desc&limit=1'
      );
    } catch {
      effectiveRows = await apiGetAuth(
        '/rest/v1/user_subscriptions?select=plan_code,status,provider,external_subscription_id,external_customer_id,period_end,updated_at,created_at&status=in.(active,trialing)&order=updated_at.desc,created_at.desc&limit=1'
      );
    }
    let row = Array.isArray(effectiveRows) ? effectiveRows[0] : effectiveRows;
    if (!row) {
      let latestRows = null;
      try {
        latestRows = await apiGetAuth(
          '/rest/v1/user_subscriptions?select=plan_code,status,provider,external_subscription_id,external_customer_id,period_end,cancel_at_period_end,updated_at,created_at&order=updated_at.desc,created_at.desc&limit=1'
        );
      } catch {
        latestRows = await apiGetAuth(
          '/rest/v1/user_subscriptions?select=plan_code,status,provider,external_subscription_id,external_customer_id,period_end,updated_at,created_at&order=updated_at.desc,created_at.desc&limit=1'
        );
      }
      row = Array.isArray(latestRows) ? latestRows[0] : latestRows;
    }
    if (!row) {
      return { planCode: 'explorer', status: 'active', provider: '', externalSubscriptionId: '', externalCustomerId: '', cancelAtPeriodEnd: false };
    }
    return {
      planCode: withDefaults(row?.plan_code),
      status: String(row?.status || 'active'),
      provider: String(row?.provider || ''),
      externalSubscriptionId: String(row?.external_subscription_id || ''),
      externalCustomerId: String(row?.external_customer_id || ''),
      periodEnd: row?.period_end || null,
      cancelAtPeriodEnd: Boolean(row?.cancel_at_period_end)
    };
  } catch {
    return { planCode: 'explorer', status: 'active', provider: '', externalSubscriptionId: '', externalCustomerId: '', cancelAtPeriodEnd: false };
  }
}

export async function manageMyPaddleSubscription({ action, targetPlanCode }) {
  return apiPostAuth('/functions/v1/paddle-subscription-manage', {
    action,
    ...(targetPlanCode ? { target_plan_code: targetPlanCode } : {})
  });
}

export function getPlanLabel(planCode, language = 'ro') {
  const code = withDefaults(planCode);
  const isRo = language === 'ro';
  if (code === 'starter') return isRo ? 'Starter' : 'Starter';
  if (code === 'premium') return isRo ? 'Premium Mindset' : 'Premium Mindset';
  return isRo ? 'Explorer' : 'Explorer';
}

export function getPlanUiCards(language = 'ro') {
  const isRo = language === 'ro';
  return [
    {
      code: 'explorer',
      name: isRo ? 'Explorer' : 'Explorer',
      oldPrice: isRo ? '5€ / lună' : '€5 / month',
      newPrice: isRo ? 'Gratuit' : 'Free',
      launchLabel: isRo ? 'Founding members' : 'Founding members',
      cta: isRo ? 'Începe gratuit' : 'Start free',
      features: isRo
        ? ['Primele 2 lecții din fiecare modul', 'Acces puzzle limitat', 'Progres de bază']
        : ['First 2 lessons in each module', 'Limited puzzle access', 'Basic progress tracking']
    },
    {
      code: 'starter',
      name: isRo ? 'Starter' : 'Starter',
      oldPrice: isRo ? '7€ / lună' : '€7 / month',
      newPrice: isRo ? '2€ / lună' : '€2 / month',
      launchLabel: isRo ? 'Preț de lansare' : 'Launch price',
      cta: isRo ? 'Upgrade la Starter' : 'Upgrade to Starter',
      features: isRo
        ? ['Modulele 1-2 complet', 'Puzzle nelimitat beginner/intermediate', 'Quiz complet + audio premium']
        : ['Full Modules 1-2', 'Unlimited beginner/intermediate puzzles', 'Full quizzes + premium audio']
    },
    {
      code: 'premium',
      name: isRo ? 'Premium Mindset' : 'Premium Mindset',
      oldPrice: isRo ? '12€ / lună' : '€12 / month',
      newPrice: isRo ? '4€ / lună' : '€4 / month',
      launchLabel: isRo ? 'Preț de lansare' : 'Launch price',
      cta: isRo ? 'Activează Premium' : 'Activate Premium',
      features: isRo
        ? ['Toate modulele + categoriile puzzle', 'Antrenament complet și analytics', 'Conținut nou prioritar']
        : ['All modules + puzzle categories', 'Full training and analytics', 'Priority new content']
    }
  ];
}
